import type { TranslationTree } from '../../types';

export const mlRul: TranslationTree = {
  machineLearning: {
    modelPrediction: {
      title: 'Prédiction de durée de vie',
      subtitle:
        "Usure d'un outil estimée depuis son historique de traçabilité, ou durée de vie restante prédite depuis les signaux de la machine.",
      recent: {
        title: 'Prédictions récentes',
        runsTitle: 'Exécutions récentes',
        when: 'Quand',
        tool: 'Outil',
        model: 'Modèle',
        value: 'Résultat',
        helpful: 'Prédiction utile',
        notHelpful: 'Prédiction erronée',
      },
      views: { wear: "Usure de l'outil", signals: 'Signaux en direct' },
    },
  },
  mlRul: {
    context: {
      badge: 'Contexte',
      reset: 'Réinitialiser',
      step1Label: 'Machine',
      step2Label: 'Processus',
      step3Label: 'Outil',
      step1Placeholder: 'Sélectionner une machine',
      step2Placeholder: 'Sélectionner un processus',
      step3PlaceholderManual: 'Saisir un identifiant d’outil',
      step3FromProcess: 'Depuis le processus',
      step3ManualId: 'ID manuel',
      step3Mode: "Choix de l'outil",
      step3ManualHelper:
        'Saisissez l’identifiant de l’outil tel qu’il est imprimé sur l’étiquette.',
      step3NoTools: 'Aucun outil affecté à ce processus pour l’instant.',
      step2NoProcesses: 'Aucun processus n’est en cours sur cette machine.',
      runButton: 'Lancer l’inférence',
      runButtonLoading: 'Inférence en cours…',
      cancelButton: 'Annuler',
      retryButton: 'Réessayer',
      step4Label: 'Conditions de coupe',
      step4BadgeDefaults: 'défauts',
      step4BadgeFromProcess: 'depuis le processus',
      step4BadgeCustom: 'personnalisé',
      step4ResetToDefaults: 'Réinitialiser aux valeurs par défaut',
      step4PreFilled: 'pré-rempli',
      signalSourceLabel: 'Source des signaux',
      signalSourcePending: 'Affichée une fois la machine choisie.',
      signalSourceMissing:
        "Cette machine n'a pas de source de signaux, aucune prédiction ne peut donc être lancée. Liez-en une dans Atelier → Machines.",
      runNeedsSignalSource: 'Liez une source de signaux à cette machine pour lancer la prédiction.',
      params: {
        fz: 'Avance par dent',
        ap: 'Profondeur de passe axiale',
        ae: 'Profondeur de passe radiale',
        vc: 'Vitesse de coupe',
        nbDents: 'Nombre de dents',
        diameterCut: 'Diamètre de l’outil',
        units: {
          fz: 'mm/z',
          ap: 'mm',
          ae: 'mm',
          vc: 'm/min',
          nbDents: '—',
          diameterCut: 'mm',
        },
      },
    },
    tool: {
      unknownReference: 'Référence inconnue',
      lastUsed: 'Dernière utilisation le {{date}}',
      lastUsedNever: 'Jamais utilisé',
      statusInUse: 'En cours d’utilisation',
      statusIdle: 'Inactif',
      statusScrapped: 'Mis au rebut',
    },
    result: {
      badgeIdle: 'En attente d’inférence',
      badgeRunning: 'Inférence en cours',
      badgeCompleted: 'Inférence terminée',
      badgeFailed: 'Inférence échouée',
      idleTitle: 'Choisissez une machine, un processus et un outil',
      idleDescription: 'La durée de vie résiduelle s’affichera ici une fois l’inférence lancée.',
      runningOnTool: 'Inférence en cours sur {{toolId}}',
      rulLabel: 'Durée de vie résiduelle',
      secondsUnit: 'secondes',
      humanReadable: 'environ {{hours}} heures {{minutes}} minutes de temps de coupe restant',
      humanReadableSeconds: 'environ {{seconds}} secondes de temps de coupe restant',
      humanReadableDays: 'environ {{days}} jours {{hours}} heures de temps de coupe restant',
      contextChipMachine: 'Machine',
      contextChipProcess: 'Processus',
      contextChipTool: 'Outil',
      contextChipJob: 'Tâche',
      errorTitle: 'Échec de l’inférence',
      window: {
        part: 'Pièce {{part}}',
        passes: { one: '{{count}} passe', other: '{{count}} passes' },
        toolVolume: "Volume de l'outil {{volume}} cm³",
      },
    },
    signal: {
      badge: 'Signal d’inférence',
      helper: '{{passes}} dernières passes · telles que lues par le modèle',
      helperPart:
        '{{passes}} dernières passes de la pièce {{part}} · telles que lues par le modèle',
      openLink: 'Ouvrir dans Visualisation des signaux →',
      errorTitle: 'Signal indisponible',
      errorRetry: 'Réessayer',
    },
    errors: {
      missingSignalSource:
        "Cette machine n'a pas de source de signaux. Liez-en une dans Atelier → Machines.",
      noSignalData:
        'La base de signaux ne contient aucune donnée pour la source de cette machine sur les 90 derniers jours.',
      noMachiningPass:
        "Aucune passe d'usinage connue du modèle. Les {{seconds}} dernières secondes de la pièce {{part}} ne contiennent aucune passe du bloc de laboratoire (|X|, |Y| ≤ 13 mm, Z ≤ 0). Le modèle en direct n'a été entraîné que sur ces passes.",
      noMachiningPassGeneric:
        "Aucune passe d'usinage connue du modèle dans la dernière pièce de la machine : aucune passe du bloc de laboratoire (|X|, |Y| ≤ 13 mm, Z ≤ 0). Le modèle en direct n'a été entraîné que sur ces passes.",
    },
  },
  mlWear: {
    context: {
      tool: 'Outil',
      toolPlaceholder: 'Choisir un outil',
      toolTraces: {
        one: '{{count}} trace · dernière le {{date}}',
        other: '{{count}} traces · dernière le {{date}}',
      },
      toolNoTrace: 'Aucune trace pour cet outil.',
      toolsLoadError: "La liste des outils n'a pas pu être chargée.",
      diameter: 'Diamètre de coupe',
      diameterField: 'Ø de coupe',
      required: 'obligatoire',
      diameterSource: 'Source du diamètre',
      modeAdaptcut: 'Outil Adaptcut',
      modeManual: 'Saisi',
      adaptcutPlaceholder: 'Choisir un outil Adaptcut',
      adaptcutSearch: 'Rechercher un nom ou une référence',
      adaptcutList: 'Outils Adaptcut',
      adaptcutNoMatch: 'Aucun outil Adaptcut ne correspond.',
      adaptcutSameReference: 'même référence',
      adaptcutTag: 'Adaptcut',
      adaptcutLine: 'Ø coupe {{cut}} · {{teeth}} dents · Ø queue {{shank}}',
      adaptcutNoDiameter:
        "Cet outil Adaptcut n'a pas de diamètre de coupe. Saisissez-le ci-dessous.",
      adaptcutError: "La liste des outils Adaptcut n'a pas pu être chargée.",
      retry: 'Réessayer',
      diameterHelpUsed: "Utilisé pour tout l'historique et pour la prochaine opération.",
      diameterHelpRequired: "Saisissez le diamètre de coupe de l'outil.",
      diameterHelpNoMatch:
        'Aucun outil Adaptcut ne porte la référence {{reference}} : saisissez le diamètre de coupe ou choisissez un autre outil Adaptcut.',
      diameterHelpAdaptcutDown: 'Adaptcut est injoignable : saisissez le diamètre de coupe.',
      diameterHelpPick: 'Choisissez un outil Adaptcut, ou saisissez le diamètre.',
      next: 'Prochaine opération',
      optional: 'facultatif',
      fromTrace: 'dernière trace',
      edited: 'modifié',
      volumePerPiece: 'Volume par pièce',
      plannedPieces: 'Pièces prévues',
      nextHelpFromTrace: 'Volume par pièce = volume ÷ pièces de la dernière trace.',
      nextHelpIgnored: 'ap et vc ne changent pas la prédiction.',
      backToTrace: 'Reprendre la dernière trace',
      save: 'Enregistrer la prédiction',
      saving: 'Enregistrement…',
      saved: 'Prédiction enregistrée',
      saveError: "La prédiction n'a pas pu être enregistrée.",
      needsDiameter: "Indiquez le diamètre de coupe pour calculer l'usure.",
      units: { mm: 'mm', mmPerTooth: 'mm/dent', cm3: 'cm³', pieces: 'pcs' },
    },
    result: {
      idle: 'Choisissez un outil pour estimer son usure.',
      needsDiameter: "Indiquez le diamètre de coupe (étape 1) pour calculer l'usure.",
      loading: "Calcul de l'usure…",
      modelUnavailableTitle: "Le modèle d'usure n'est pas déployé sur ce serveur.",
      modelUnavailable:
        "Un administrateur doit enregistrer wear_factor dans le registre de modèles. L'historique de l'outil reste visible dans Traçabilité.",
      error: "L'usure n'a pas pu être calculée.",
      reportedWorn: 'Outil déclaré usé dans sa trace du {{date}}.',
      reportedBroken: 'Outil déclaré cassé dans sa trace du {{date}}.',
      reportedDetail:
        "Le modèle estime son usure à {{wear}}, avec {{pWorn}} de probabilité d'être usé.",
      outOfRangeTitle:
        "Hors du domaine d'ajustement du modèle : le résultat est une extrapolation.",
      outOfRange: {
        material: 'matière {{values}} (ajusté sur {{fitted}})',
        ap: 'ap {{values}} mm (ajusté sur {{fitted}} mm)',
        ae: 'ae {{values}} mm (ajusté sur {{fitted}} mm)',
        diameter: 'diamètre {{values}} mm (ajusté sur {{fitted}} mm)',
        hm: 'épaisseur de copeau hm {{values}} µm (ajusté sur {{fitted}} µm)',
      },
      stateTitle: "État de l'outil",
      tracesCounted: {
        one: '{{count}} trace comptée',
        other: '{{count}} traces comptées',
      },
      tracesSkipped: {
        one: '{{count}} ignorée',
        other: '{{count}} ignorées',
      },
      wear: 'Usure estimée',
      wearOfLife: 'de la durée de vie prédite',
      pWorn: "Probabilité d'être déjà usé",
      milled: 'Déjà usiné',
      milledPieces: {
        one: '{{count}} pièce',
        other: '{{count}} pièces',
      },
      gaugeLabel: 'Usure {{wear}} de la durée de vie prédite',
      gaugePrudent: 'prudent {{value}}',
      gaugeEnd: 'fin de vie prédite',
      capacityTitle: 'Encore usinable',
      capacitySettings: 'fz {{fz}} · ae {{ae}} · Ø {{d}} → hm {{hm}} µm',
      outOfRangeTag: 'hors domaine',
      overestimatedTag: 'surestimé',
      lowerBoundTag: 'minimum',
      missingCell: 'manquant',
      incomplete: {
        titleSome: {
          one: "Usure sous-estimée : {{count}} trace sur {{total}} n'est pas comptée",
          other: 'Usure sous-estimée : {{count}} traces sur {{total}} ne sont pas comptées',
        },
        titleNone: {
          one: "Aucune trace utilisable : la seule trace n'est pas comptée",
          other: 'Aucune trace utilisable : aucune des {{count}} traces ne compte',
        },
        consequence:
          "L'usure et la probabilité affichées sont des minimums ; ce que l'outil peut encore usiner est surestimé.",
        missing: { one: '{{fields}} manquant', other: '{{fields}} manquants' },
        link: "Compléter les traces dans la fiche de l'outil →",
      },
      capacityEmpty:
        "Indiquez fz et ae de la prochaine opération pour estimer ce que l'outil peut encore usiner.",
      prudent: 'Prudent',
      prudentHelp: '1 outil sur 10 serait usé avant.',
      median: 'Médian',
      medianHelp: 'La moitié des outils seraient usés avant.',
      piecesUnit: 'pièces',
      needsVolumePerPiece: 'Indiquez le volume par pièce pour compter les pièces.',
      planned:
        "Après les {{pieces}} pièces prévues ({{volume}} cm³) : usure {{wear}}, probabilité d'être usé {{pWorn}}.",
      plannedRisky: 'Risque supérieur à 1 sur 10.',
      plannedSafe: 'Dans la marge prudente.',
      historyTitle: "Historique de l'outil",
      historyHelp: 'Chaque trace ajoute son volume divisé par la durée de vie à ses réglages.',
      historyEmpty: "Aucune trace : l'outil est considéré neuf.",
      columns: {
        date: 'Date',
        operation: 'Opération',
        machine: 'Machine',
        material: 'Matière',
        ap: 'ap',
        ae: 'ae',
        fz: 'fz',
        hm: 'hm µm',
        pieces: 'Pièces',
        volume: 'Volume cm³',
        wearAdded: 'Usure +',
        cumulative: 'Cumul',
        state: 'État',
      },
      skipped: 'non comptée : {{reason}}',
      skippedReasons: { missing_volume: 'volume manquant', missing_settings: 'ae ou fz manquant' },
      provenance: 'Modèle {{name}} {{version}} du registre',
      provenanceFitted: 'ajusté sur {{tools}} outils en {{materials}}, Ø {{diameter}} mm',
      provenanceError: 'erreur de durée de vie {{error}} min en validation croisée',
    },
    recent: {
      left: 'Encore usinable (médian)',
      settings: 'Réglages',
      leftPieces: '{{pieces}} pièces · {{volume}} cm³',
      leftVolume: '{{volume}} cm³',
    },
  },
};
